
import { toZonedTime } from 'date-fns-tz';
import { createPerformanceLogger } from '@/lib/utils/performance-logger';

const logger = createPerformanceLogger('DayOfWeekUtils');

/**
 * Get day of week (0-6, Sunday=0) in a specific timezone
 * This ensures the day-of-week calculation happens in the organizer's timezone
 * FIXED: Handle timezone boundary crossings correctly
 */
export function getTimezoneAwareDayOfWeek(date: Date, timezone: string): number {
  try {
    // Convert the date directly to the target timezone without double conversion
    // This preserves the original date's calendar day in the target timezone
    const zonedDate = toZonedTime(date, timezone);
    const dayOfWeek = zonedDate.getDay();
    
    logger.debug(`Day of week calculation (TIMEZONE-FIXED):`, {
      originalDate: date.toISOString(),
      timezone,
      zonedDate: zonedDate.toISOString(),
      calculatedDayOfWeek: dayOfWeek,
      dayName: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][dayOfWeek]
    });
    
    return dayOfWeek;
  } catch (error) {
    logger.error(`Error calculating day of week for timezone ${timezone}:`, error);
    // Fallback to the original date's day of week
    return date.getDay();
  }
}

/**
 * Validate that day-of-week mapping is working correctly
 */
export function validateDayOfWeekMapping(date: Date, timezone: string, expectedDay?: number): boolean {
  const calculatedDay = getTimezoneAwareDayOfWeek(date, timezone);
  
  if (expectedDay !== undefined && calculatedDay !== expectedDay) {
    logger.warn(`Day of week mapping validation failed:`, {
      date: date.toISOString(),
      timezone,
      expected: expectedDay,
      calculated: calculatedDay
    });
    return false;
  }
  
  return true;
}

/**
 * Debug helper to show day-of-week mapping for a date range
 */
export function debugDayOfWeekMapping(startDate: Date, days: number, timezone: string): void {
  logger.debug(`Day of week mapping debug for ${timezone}:`);
  
  for (let i = 0; i < days; i++) {
    const date = new Date(startDate);
    date.setDate(startDate.getDate() + i);
    
    const utcDay = date.getDay();
    const timezoneDay = getTimezoneAwareDayOfWeek(date, timezone);
    
    logger.debug(`Date ${date.toDateString()}: UTC day=${utcDay}, ${timezone} day=${timezoneDay}`);
  }
}
